// src/pages/RestaurantOwner/Categories.jsx - SUPER OPTIMIZED

import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Tag, Loader } from 'lucide-react';
import StorageService from '../../services/StorageService';

const Categories = ({ restaurantId }) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');

  useEffect(() => {
    loadCategories();
  }, [restaurantId]);

  // ✅ OPTIMIZED: Use getRestaurant instead of full getData
  const loadCategories = async () => {
    try {
      setLoading(true);
      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      if (restaurant) {
        setCategories(restaurant.categories || []);
        setSubcategories(restaurant.subcategories || ['Veg', 'Non-Veg', 'Vegan']);
      }
    } catch (error) {
      console.error('❌ Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ OPTIMIZED: Instant UI update + background save
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) return;

    const newCategory = categoryName.trim();

    // Check duplicate
    if (editingCategory === null && categories.includes(newCategory)) {
      alert('Category already exists!');
      return;
    }

    try {
      setSaving(true);

      // ✅ OPTIMISTIC UPDATE - Update UI immediately
      let updatedCategories;
      if (editingCategory !== null) {
        updatedCategories = [...categories];
        updatedCategories[editingCategory] = newCategory;
      } else {
        updatedCategories = [...categories, newCategory];
      }

      setCategories(updatedCategories);
      setCategoryName('');
      setEditingCategory(null);
      setShowCategoryModal(false);

      // ✅ Save to Firebase in background
      await StorageService.updateRestaurantCategories(
        restaurantId,
        updatedCategories,
        subcategories
      );

       
    } catch (error) {
      console.error('❌ Error saving category:', error);
      alert('Failed to save category. Please try again.');
      // ✅ Reload on error
      await loadCategories();
    } finally {
      setSaving(false);
    }
  };

  // ✅ OPTIMIZED: Instant UI update + background save
  const handleAddSubcategory = async (e) => {
    e.preventDefault();
    if (!subcategoryName.trim()) return;

    const newSubcategory = subcategoryName.trim();

    // Check duplicate
    if (editingSubcategory === null && subcategories.includes(newSubcategory)) {
      alert('Subcategory already exists!');
      return;
    }

    try {
      setSaving(true);

      // ✅ OPTIMISTIC UPDATE - Update UI immediately
      let updatedSubcategories;
      if (editingSubcategory !== null) {
        updatedSubcategories = [...subcategories];
        updatedSubcategories[editingSubcategory] = newSubcategory;
      } else {
        updatedSubcategories = [...subcategories, newSubcategory];
      }

      setSubcategories(updatedSubcategories);
      setSubcategoryName('');
      setEditingSubcategory(null);
      setShowSubcategoryModal(false);

      // ✅ Save to Firebase in background
      await StorageService.updateRestaurantCategories(
        restaurantId,
        categories,
        updatedSubcategories
      );

       
    } catch (error) {
      console.error('❌ Error saving subcategory:', error);
      alert('Failed to save subcategory. Please try again.');
      // ✅ Reload on error
      await loadCategories();
    } finally {
      setSaving(false);
    }
  };

  // ✅ OPTIMIZED: Instant UI update + background save
  const handleDeleteCategory = async (index) => {
    if (window.confirm('Are you sure? This will not delete menu items, only the category label.')) {
      try {
        setSaving(true);

        // ✅ OPTIMISTIC UPDATE - Update UI immediately
        const updatedCategories = categories.filter((_, i) => i !== index);
        setCategories(updatedCategories);

        // ✅ Save to Firebase in background
        await StorageService.updateRestaurantCategories(
          restaurantId,
          updatedCategories,
          subcategories
        );

         
      } catch (error) {
        console.error('❌ Error deleting category:', error);
        alert('Failed to delete category. Please try again.');
        // ✅ Reload on error
        await loadCategories();
      } finally {
        setSaving(false);
      }
    }
  };

  // ✅ OPTIMIZED: Instant UI update + background save
  const handleDeleteSubcategory = async (index) => {
    if (window.confirm('Are you sure? This will not delete menu items, only the subcategory label.')) {
      try {
        setSaving(true);

        // ✅ OPTIMISTIC UPDATE - Update UI immediately
        const updatedSubcategories = subcategories.filter((_, i) => i !== index);
        setSubcategories(updatedSubcategories);

        // ✅ Save to Firebase in background
        await StorageService.updateRestaurantCategories(
          restaurantId,
          categories,
          updatedSubcategories
        );

         
      } catch (error) {
        console.error('❌ Error deleting subcategory:', error);
        alert('Failed to delete subcategory. Please try again.');
        // ✅ Reload on error
        await loadCategories();
      } finally {
        setSaving(false);
      }
    }
  };

  const openEditCategory = (index) => {
    setEditingCategory(index);
    setCategoryName(categories[index]);
    setShowCategoryModal(true);
  };

  const openEditSubcategory = (index) => {
    setEditingSubcategory(index);
    setSubcategoryName(subcategories[index]);
    setShowSubcategoryModal(true);
  };

  const closeModals = () => {
    setShowCategoryModal(false);
    setShowSubcategoryModal(false);
    setCategoryName('');
    setSubcategoryName('');
    setEditingCategory(null);
    setEditingSubcategory(null);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Categories & Subcategories</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Categories Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Tag className="w-6 h-6 text-orange-500" />
              Categories
            </h2>
            <button
              onClick={() => setShowCategoryModal(true)}
              disabled={saving}
              className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50 transition"
            >
              <Plus className="w-4 h-4" /> Add Category
            </button>
          </div>

          <div className="space-y-3">
            {categories.length > 0 ? (
              categories.map((cat, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                  <span className="font-medium text-gray-800">{cat}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditCategory(index)}
                      disabled={saving}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(index)}
                      disabled={saving}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No categories yet. Add your first category!</p>
            )}
          </div>
        </div>

        {/* Subcategories Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Tag className="w-6 h-6 text-green-500" />
              Subcategories
            </h2>
            <button
              onClick={() => setShowSubcategoryModal(true)}
              disabled={saving}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center gap-2 disabled:opacity-50 transition"
            >
              <Plus className="w-4 h-4" /> Add Subcategory
            </button>
          </div>

          <div className="space-y-3">
            {subcategories.length > 0 ? (
              subcategories.map((sub, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                  <span className="font-medium text-gray-800">{sub}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditSubcategory(index)}
                      disabled={saving}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubcategory(index)}
                      disabled={saving}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No subcategories yet. Add your first subcategory!</p>
            )}
          </div>
        </div>
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingCategory !== null ? 'Edit Category' : 'Add Category'}
              </h2>
              <button onClick={closeModals} className="text-gray-500 hover:text-gray-700" disabled={saving}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category Name</label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Main Course, Appetizers"
                  required
                  disabled={saving}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>{editingCategory !== null ? 'Update' : 'Add'} Category</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingSubcategory !== null ? 'Edit Subcategory' : 'Add Subcategory'}
              </h2>
              <button onClick={closeModals} className="text-gray-500 hover:text-gray-700" disabled={saving}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddSubcategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory Name</label>
                <input
                  type="text"
                  value={subcategoryName}
                  onChange={(e) => setSubcategoryName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Veg, Non-Veg, Spicy"
                  required
                  disabled={saving}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>{editingSubcategory !== null ? 'Update' : 'Add'} Subcategory</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;